import { PageProps } from '../../components/types'
import HeaderDetail from '../../components/HeaderDetail'
import { PicklistItemQaCheckMvColumn } from '../../constants/Column'
import Modal from '../../components/Modal'
import Comment from '../../components/Comment'
import Badge from '../../components/Badge'

import OrderPicklistReducer, { 
  INITIAL_STATE, 
  PicklistAction
} from '../../states/PicklistReducer'
import { 
  postComment, 
  getRemotePicklistById,
  getMovementsById,
  qaCheckItemMovement,
  qaUncheckItemMovement,
  qaCheckPicklist,
  rollbackQcCheckPicklist,
  updateStatusComment
} from '../../states/PicklistAction'

import DataGrid, { RowsChangeData } from 'react-data-grid'

import { 
  useReducer, useEffect, useCallback, useState
} from 'react'
import { AiOutlineCheckCircle } from 'react-icons/ai'
import { HiMagnifyingGlass } from 'react-icons/hi2'

import { groupBy as rowGrouper } from 'lodash';

import { DgtPdOrderPicklistMovement } from "../../models"
import { formatDateFromUnix } from '../../utilities/Format'

const PicklistQaCheckDetail = ({ title, id, username, readOnly } : PageProps) => {
  const [ state, dispatch ] = useReducer(
    OrderPicklistReducer, INITIAL_STATE);
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>(
    () => new Set<unknown>([''])
  )

  useEffect(() => {
    const fetchPicklists = async (id: string) => {
      const { header, items, comments } = await getRemotePicklistById(id!)
      dispatch({ type: PicklistAction.Inited, payload: { header, 
        items: items.filter(pl => pl.materialType !== 'BULK'),
        comments
      } })
    }

    const fetchMovements = async (orderId: string) => {
      const mvs = await getMovementsById(orderId)
      dispatch({ 
        type: PicklistAction.PicklistItemMovementsUpdated, 
        payload: { 
          itemMovements: mvs.filter(itm => itm.movementType !== '262')
            .filter(itm => itm.materialType !== 'BULK')
            .filter(itm => !itm.extraRefId)
        }})
    }

    fetchPicklists(id!)
    fetchMovements(id!)
  }, [ id ])
  
  useEffect(() => {
    if (state.itemMovements.length > 0) {
      setExpandedGroupIds(new Set(state.itemMovements.map(itm => itm.materialCode)))
    }
  }, [ state.itemMovements ])

  const openDialog = () => {
    dispatch({ type: PicklistAction.OpenDialog })
  }

  const closeDialog = () => {
    dispatch({ type: PicklistAction.CloseDialog })
  }

  const onPostComment = useCallback(async (comment: string) => {
    const commentResponse = await postComment(
      state.header?.pk!,
      comment, 
      username!
    )
    dispatch({ type: PicklistAction.PostComment, payload : { comments: [commentResponse] } })
  }, [ state.header?.pk, username ])

  const onActiveComment = useCallback(async (orderId: string, skCommentId: string, active: boolean, version: number) => {
		const updatedComment = await updateStatusComment(orderId, skCommentId, active, version)
    dispatch({ type: PicklistAction.UpdateComment, payload: { comments: [updatedComment] } })
  }, [])

  const onRowsChange = async (rows: DgtPdOrderPicklistMovement[], 
    changedInfo: RowsChangeData<DgtPdOrderPicklistMovement>) => {
    const changedRowIdx = changedInfo.indexes[0]
    if (changedRowIdx < 0) return
    
    const changedRow = rows[changedRowIdx]
    try {
      const updated = changedRow.qualityCheckedDateTime! > 0 ?
        await qaCheckItemMovement(changedRow, username!) : await qaUncheckItemMovement(changedRow)
      if (!changedRow.qualityCheckedDateTime || changedRow.qualityCheckedDateTime === 0) {
        if (state.header?.qualityCheckedBy && state.header?.qualityCheckedDateTime) {
          const updated = await rollbackQcCheckPicklist(state.header!)
          dispatch({ type: PicklistAction.PicklistUpdated, payload: { header: updated } })
        }
      }
      dispatch({ 
        type: PicklistAction.PicklistItemMovementsUpdated,
        payload: {
          itemMovements: rows.map(row => row.sk === updated.sk ? updated : row)
        }
      })
    } catch (err) {
      alert('เกิดความผิดพลาด กรุณาตรวจสอบ Internet และลองอีกครั้ง')
    }
  }

  const onApproved = async () => {
    const updated = await qaCheckPicklist(state.header!, username!)
    dispatch({ type: PicklistAction.PicklistUpdated, payload: { header: updated } })
  }

  const { header, showConfirmDialog, comments, itemMovements, items } = state

  const withDrawItems = new Set(itemMovements.map(itm => itm.materialCode))
  const disabledComplete = itemMovements.length !== itemMovements.filter(mv => mv.qualityCheckedDateTime).length

  let columns = Array.from(PicklistItemQaCheckMvColumn)
  if (readOnly) columns.splice(-1)

  return <div className="page-container">
    <HeaderDetail title={`${title} Order : ${id}`} header={header} />
    <h6 className="py-1 font-light">PD Checked by<span className='mx-2 font-bold lg:mx-3 xl:ml-11'>{header?.checkedBy}</span>
      <span className='text-xs italic text-muted'>({formatDateFromUnix(header?.checkedDateTime)})</span>
    </h6>
    <div className='flex items-end justify-between mt-1 mb-2'>
      <small>จ่าย {withDrawItems.size} จากทั้งหมด {items.length} รายการ</small>
      {readOnly ? null : <button className='gap-1 btn-success' onClick={openDialog} disabled={disabledComplete}>
        <AiOutlineCheckCircle />กดเพื่อปล่อย PASS
      </button>}
    </div> 
    <DataGrid className='mb-3'
      style={{ height: "100%", contain: "none" }}
      columns={columns}
      rows={itemMovements}
      rowKeyGetter={(row) => row.id}
      groupBy={['materialCode']}
      rowGrouper={rowGrouper}
      expandedGroupIds={expandedGroupIds}
      onExpandedGroupIdsChange={setExpandedGroupIds}
      defaultColumnOptions={{ resizable: true }}
      onRowsChange={onRowsChange}
      rowClass={row => row.qualityCheckedDateTime! > 0 ? ' bg-green-100' : ''}
    />
    <small>รายการที่ไม่มีจ่าย</small>
    <div className='flex flex-row flex-wrap gap-3 mt-2 mb-3'>
      {items
        .filter(itm => !Array.from(withDrawItems).includes(itm.materialCode))
        .map(itm => 
          <Badge key={itm.id} variant='outline-muted'>
            {`${itm.materialCode} - ${itm.materialDescription} = ${itm.requestQty} ${itm.materialUnit}`}
          </Badge>
        )
      }
    </div>
    <Comment comments={comments.sort((a, b) => a.sk.localeCompare(b.sk))} onPost={onPostComment} onActive={onActiveComment} username={username}/>
    <Modal show={showConfirmDialog} cancelText='ยกเลิก'
      onHide={closeDialog}
      okText="ใช่"
      okTheme='success'
      okAction={onApproved}
      title={`ยืนยันการตรวจสอบ Order ${id}`}>
      <div className='flex items-center gap-2'>
        <HiMagnifyingGlass size={50} className='text-green-500 basis-1/6' />
        <div className='space-y-2 basis-5/6'>
          <h6>คุณกำลังยืนยันว่าตรวจสอบ RM ของใบเบิกของ ORDER <span className='font-bold '>{id}</span> ครบถ้วนแล้ว</h6>
          <h6>คุณยืนยันใช่หรือไม่ ?</h6>
        </div>
      </div>
    </Modal>
  </div>
}

export default PicklistQaCheckDetail